// client/src/lib/chunkReloadGuard.ts
/**
 * Chunk-load recovery: when a lazy chunk 404s because the bundle was swapped under a live document
 * (a Capgo OTA in the app, a deploy on the web), reload ONCE to pick up the current chunk graph.
 *
 * The loop guard is TIME-BOXED (60 s), not once-per-session: the old ErrorBoundary key was never
 * cleared, so after the first recovery every later OTA swap in that session showed the error screen
 * (Sentry LAMIRAGECO-DV, AppPushPrompt-*.js, 2026-11). Pure — storage and clock are injected.
 */
export const CHUNK_RELOAD_KEY = "__chunk_reload_at__";
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

type Store = { getItem(k: string): string | null; setItem(k: string, v: string): void };

/** true = reload now (and the time is stamped); false = we reloaded < 60 s ago, or cannot guard a loop. */
export function shouldReloadForChunkError(storage: Store | null | undefined, now: number): boolean {
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(CHUNK_RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < CHUNK_RELOAD_WINDOW_MS) return false;
    storage.setItem(CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const msg = String((error as { message?: unknown }).message ?? "");
  const name = String((error as { name?: unknown }).name ?? "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /dynamically imported module/i.test(msg) ||
    /Unable to preload CSS/i.test(msg)
  );
}

/** sessionStorage, or null where it is unavailable (private mode, sandboxed frame). */
export function sessionStore(): Store | null {
  try { return typeof sessionStorage !== "undefined" ? sessionStorage : null; } catch { return null; }
}

/**
 * In-memory "a reload is already scheduled" flag (per document — a reload clears it). Once set, every
 * later vite:preloadError in the same failure is suppressed, the error boundaries render nothing
 * instead of flashing "Something went wrong", and nothing is reported: after a PREVENTED preload,
 * React.lazy receives `undefined` and throws a non-chunk "Lazy element type must resolve…" error
 * that is pure noise.
 */
let reloadPending = false;
export function markChunkReloadPending(): void { reloadPending = true; }
export function isChunkReloadPending(): boolean { return reloadPending; }
/** Test-only. */
export function resetChunkReloadPendingForTests(): void { reloadPending = false; }

/** client-app's window 'vite:preloadError' listener. */
export function makePreloadErrorHandler(deps: {
  storage: () => Store | null;
  now: () => number;
  reload: () => void;
}): (event: { preventDefault(): void }) => void {
  return (event) => {
    if (reloadPending) { event.preventDefault(); return; }
    // Inside the 60 s window with no reload scheduled: let the error propagate (lazyWithRetry, boundaries).
    if (!shouldReloadForChunkError(deps.storage(), deps.now())) return;
    reloadPending = true;
    event.preventDefault();
    deps.reload();
  };
}

export type BoundaryErrorAction = "reload" | "wait" | "show";

/** What an error boundary does with a caught error. "wait" = a reload is coming: render nothing. */
export function boundaryErrorAction(error: unknown, pending: boolean, storage: Store | null, now: number): BoundaryErrorAction {
  if (pending) return "wait";
  if (isChunkLoadError(error) && shouldReloadForChunkError(storage, now)) return "reload";
  return "show";
}

/** Report to Sentry? Never a chunk error (an expected OTA/deploy swap), never anything while a reload is pending. */
export function shouldReportBoundaryError(error: unknown, pending: boolean): boolean {
  return !pending && !isChunkLoadError(error);
}
